import { keys } from 'remeda';

import { CAPTCHA_TEST_KEYS } from '@lib/config';
import { getLogger, log, Logger } from '@lib/services/logger';
import { isServer } from '@lib/utils';

import { LANGUAGES_AVAILABLE } from '../i18n/i18n.settings';
import type { Locale } from '../i18n/types';

import {
  AiChatbotConfig,
  ClientConfig,
  ClientConfigAnalytics,
  ClientConfigAnalyticsSolution,
  ClientConfigContactForm,
  ClientConfigLanguages,
  ClientConfigSearch,
  ClientConfigSmtp,
  DefaultConfigs,
  OdooConfig
} from './configService.types';

const MATCH_TENANT_ID = /^PT__.*__TENANT_UID$/;

class ConfigService {
  private logger: Logger = {} as Logger;
  private clientConfigs: ClientConfig[] = [];
  private default: DefaultConfigs = {} as DefaultConfigs;

  constructor() {
    this.logger = getLogger('CONFIG');
    if (!isServer()) {
      this.logger.debug('access from client, not server');
      return;
    }
    this.default = this.getDefaultClientConfig();
    this.clientConfigs = this.parseClientConfigs();
    this.logger.debug(
      'Client configs loaded',
      this.clientConfigs.map(c => this.redactSecrets(c))
    );
  }

  private redactSecrets(cfg: ClientConfig) {
    const redact = (value?: string | null) => (value ? '********' : value);

    return {
      ...cfg,
      smtp: { ...cfg.smtp, password: redact(cfg.smtp.password) },
      contactForm: {
        ...cfg.contactForm,
        recaptchaSecretKey: redact(cfg.contactForm.recaptchaSecretKey)
      },
      search: { ...cfg.search, apiKey: redact(cfg.search.apiKey) },
      odoo: cfg.odoo ? { ...cfg.odoo, apiKey: redact(cfg.odoo.apiKey) } : cfg.odoo
    };
  }

  public getClientConfigs(): ClientConfig[] {
    return this.clientConfigs;
  }

  public getClientConfigByTenantUid(tenantUid: string): ClientConfig | null {
    if (LANGUAGES_AVAILABLE.includes(tenantUid as Locale)) {
      return this.getFallbackClientConfig();
    }
    const cfg = this.clientConfigs.find(c => c.tenantUid === tenantUid);

    if (!cfg) {
      this.logger.log(
        `No client config found for ID '${tenantUid}' -> falling back to fallback: ${this.default.tenantUid}`
      );

      return this.getFallbackClientConfig();
    }

    return cfg;
  }

  public getClientConfigByHost(host: string): ClientConfig | null {
    const cfg = this.clientConfigs.find(c => c.hosts.includes(host));

    if (!cfg) {
      this.logger.log(
        `No client config found for host '${host}' -> falling back to fallback: ${this.default.tenantUid}`
      );

      return this.getFallbackClientConfig();
    }

    return cfg;
  }

  public getFallbackClientConfig(): ClientConfig | null {
    if (!this.default.tenantUid) {
      this.logger.error('No default tenant ID configured: provide env param PT_FALLBACK_TENANT');
      return null;
    }

    const match = this.clientConfigs.find(c => c.tenantUid === this.default.tenantUid);

    if (!match) {
      this.logger.log(`Fallback client ID '${this.default.tenantUid}' not found`);

      return null;
    }

    return match;
  }

  private getDefaultClientConfig() {
    const cfg: DefaultConfigs = {
      tenantUid: process.env.PT_FALLBACK_TENANT || 'default',
      meilisearch: {
        enabled: this.bool(process.env.PT_DEFAULT_MEILISEARCH_ENABLED),
        url: process.env.MEILISEARCH_URL || '',
        indexPrefix: process.env.MEILISEARCH_INDEX_PREFIX || '',
        apiKey: process.env.MEILISEARCH_API_KEY || ''
      },
      smtp: {
        username: process.env.PT_DEFAULT_SMTP_USERNAME || '',
        password: process.env.PT_DEFAULT_SMTP_PASSWORD || '',
        host: process.env.PT_DEFAULT_SMTP_HOST || '',
        port: this.int(process.env.PT_DEFAULT_SMTP_PORT),
        secure: this.bool(process.env.PT_DEFAULT_SMTP_SECURE)
      },
      analytics: {
        enabled: this.bool(process.env.PT_DEFAULT_ANALYTICS_ENABLED),
        solution: process.env.PT_DEFAULT_ANALYTICS_SOLUTION as ClientConfigAnalyticsSolution,
        clientId: process.env.PT_DEFAULT_ANALYTICS_CLIENT_ID || '',
        cookieBannerEnabled: this.bool(process.env.PT_DEFAULT_ANALYTICS_COOKIE_BANNER_ENABLED),
        thirdPartyCookiesEnabled: this.bool(
          process.env.PT_DEFAULT_ANALYTICS_THIRD_PARTY_COOKIES_ENABLED
        )
      },
      contactForm: {
        recaptchEnabled: this.bool(process.env.PT_DEFAULT_CONTACT_FORM_RECAPTCHA_ENABLED),
        recaptchaSiteKey: process.env.PT_DEFAULT_CONTACT_FORM_RECAPTCHA_SITE_KEY || '',
        recaptchaSecretKey: process.env.PT_DEFAULT_CONTACT_FORM_RECAPTCHA_SECRET_KEY || ''
      },
      robotsEnabled: this.bool(process.env.PT_DEFAULT_ROBOTS_ENABLED),
      googleSiteVerificationCode: false,
      aiChatbot: {
        enabled: this.bool(process.env.PT_DEFAULT_AI_CHATBOT_ENABLED),
        apiUrl: process.env.PT_DEFAULT_AI_CHATBOT_API_URL
      },
      odoo: {
        url: process.env.PT_DEFAULT_ODOO_URL,
        db: process.env.PT_DEFAULT_ODOO_DB,
        username: process.env.PT_DEFAULT_ODOO_USERNAME,
        apiKey: process.env.PT_DEFAULT_ODOO_API_KEY,
        optiComesFrom: process.env.PT_DEFAULT_ODOO_OPTI_COMES_FROM || 'WebForm_Phoenix'
      }
    };

    if (!cfg.tenantUid) {
      this.logger.error('Missing default tenant ID: provide env param PT_FALLBACK_TENANT');
    }

    // if (!cfg.meilisearch.url) {
    //   this.logger.error(
    //     'Missing default Meilisearch URL: provide env param PT_DEFAULT_MEILISEARCH_URL'
    //   );
    // }
    // if (!cfg.meilisearch.indexPrefix) {
    //   this.logger.error(
    //     'Missing default Meilisearch index prefix: provide env param PT_DEFAULT_MEILISEARCH_INDEX_PREFIX'
    //   );
    // }
    // if (!cfg.meilisearch.apiKey) {
    //   this.logger.error(
    //     'Missing default Meilisearch API key: provide env param PT_DEFAULT_MEILISEARCH_API_KEY'
    //   );
    // }

    if (!cfg.smtp.username) {
      this.logger.error(
        'Missing default SMTP username: provide env param PT_DEFAULT_SMTP_USERNAME'
      );
    }
    if (!cfg.smtp.password) {
      this.logger.error(
        'Missing default SMTP password: provide env param PT_DEFAULT_SMTP_PASSWORD'
      );
    }
    if (!cfg.smtp.host) {
      this.logger.error('Missing default SMTP host: provide env param PT_DEFAULT_SMTP_HOST');
    }
    if (!cfg.smtp.port) {
      this.logger.error('Missing default SMTP port: provide env param PT_DEFAULT_SMTP_PORT');
    }
    if (cfg.smtp.secure === undefined) {
      this.logger.error('Missing default SMTP secure: provide env param PT_DEFAULT_SMTP_SECURE');
    }

    if (!cfg.analytics.clientId) {
      this.logger.error(
        'Missing default Analytics client ID: provide env param PT_DEFAULT_ANALYTICS_CLIENT_ID'
      );
    }
    if (!cfg.analytics.solution) {
      this.logger.error(
        'Missing default Analytics solution: provide env param PT_DEFAULT_ANALYTICS_SOLUTION'
      );
    }
    if (!cfg.analytics.cookieBannerEnabled) {
      this.logger.error(
        'Missing default Analytics cookie banner enabled: provide env param PT_DEFAULT_ANALYTICS_COOKIE_BANNER_ENABLED'
      );
    }
    if (cfg.analytics.thirdPartyCookiesEnabled === undefined) {
      this.logger.error(
        'Missing default Analytics third party cookies enabled: provide env param PT_DEFAULT_ANALYTICS_THIRD_PARTY_COOKIES_ENABLED'
      );
    }
    if (!cfg.contactForm.recaptchaSiteKey) {
      this.logger.error(
        'Missing default Contact Form reCAPTCHA site key: provide env param PT_DEFAULT_CONTACT_FORM_RECAPTCHA_SITE_KEY'
      );
    }
    if (!cfg.contactForm.recaptchaSecretKey) {
      this.logger.error(
        'Missing default Contact Form reCAPTCHA secret key: provide env param PT_DEFAULT_CONTACT_FORM_RECAPTCHA_SECRET_KEY'
      );
    }
    if (cfg.contactForm.recaptchEnabled === undefined) {
      this.logger.error(
        'Missing default Contact Form reCAPTCHA enabled: provide env param PT_DEFAULT_CONTACT_FORM_RECAPTCHA_ENABLED'
      );
    }
    if (cfg.robotsEnabled === undefined) {
      this.logger.error(
        'Missing default robots.txt enabled: provide env param PT_DEFAULT_ROBOTS_ENABLED'
      );
    }

    if (cfg.aiChatbot.enabled && !cfg.aiChatbot.apiUrl) {
      this.logger.error(
        'Missing AI Chatbot API URL: provide env param PT_DEFAULT_AI_CHATBOT_API_URL'
      );
      cfg.aiChatbot.enabled = false;
    }

    return cfg;
  }

  private parseClientConfigs() {
    const env = process.env;
    const clientConfigs: ClientConfig[] = [];
    const envKeys = keys(env);

    for (const key of envKeys) {
      if (MATCH_TENANT_ID.test(key)) {
        this.logger.debug(`Parsing client config for key '${key}'`);
        const keyId = key.replace(/PT__(.*)__TENANT_UID/, '$1');

        this.logger.debug(`Parsing client config for key ID '${keyId}'`);

        const id = env[key];
        if (!id) {
          log.error(`Missing client ID for client '${keyId}'`);
          break;
        }

        if (clientConfigs.find(c => c.tenantUid === id)) {
          log.error(`Duplicate client ID '${id}' found`);
          break;
        }

        /**
         * Hosts
         */
        let hosts: string[] = [];
        const hostsStr = env[`PT__${keyId}__HOSTS`];
        if (!hostsStr) {
          log.error(
            `Missing HOSTS for client ${keyId}. Important: hosts are required to determine client config mapping.`
          );
          break;
        } else {
          hosts = hostsStr.split(',').map(host => host.trim());
        }

        /**
         * Languages
         */
        const lngLanguagesStr = env[`PT__${keyId}__LANGUAGES`];
        if (!lngLanguagesStr) {
          log.error(`Missing LANGUAGES for client ${keyId}`);
          break;
        }
        const lngDefaultLanguage = env[`PT__${keyId}__DEFAULT_LANGUAGE`] as Locale;
        if (!lngDefaultLanguage) {
          log.error(`Missing DEFAULT_LANGUAGE for client ${keyId}`);
          break;
        }
        const lngLanguages = lngLanguagesStr.split(',').map(lng => lng.trim()) as Locale[];
        if (!lngLanguages.includes(lngDefaultLanguage)) {
          log.error(`DEFAULT_LANGUAGE must be one of the LANGUAGES for client ${keyId}`);
          break;
        }

        const languages: ClientConfigLanguages = {
          languages: lngLanguages,
          defaultLanguage: lngDefaultLanguage
        };

        /**
         * robots.txt
         */
        const robotsStr = env[`PT__${keyId}__ROBOTS_ENABLED`];
        const isRobotsEnabled = robotsStr ? robotsStr === 'true' : this.default.robotsEnabled;

        /**
         * Search
         */
        let isSearchEnabled = false;
        if (env[`PT__${keyId}__SEARCH_ENABLED`] === 'true') {
          isSearchEnabled = true;
        }
        const search: ClientConfigSearch = {
          ...this.default.meilisearch,
          enabled: isSearchEnabled
        };

        /**
         * Analytics
         */
        let isAnalyticsEnabled = this.default.analytics.enabled;
        if (env[`PT__${keyId}__ANALYTICS_ENABLED`] === 'true') {
          isAnalyticsEnabled = true;
        }
        const analyticsSolution =
          env[`PT__${keyId}__ANALYTICS_SOLUTION`] || this.default.analytics.solution;
        if (
          analyticsSolution !== 'GOOGLE_TAG_MANAGER' &&
          analyticsSolution !== 'GOOGLE_ANALYTICS'
        ) {
          log.error(`Invalid ANALYTICS_SOLUTION for client ${keyId}`, analyticsSolution);
          break;
        }
        const analyticsClientId =
          env[`PT__${keyId}__ANALYTICS_CLIENT_ID`] || this.default.analytics.clientId;
        let isCookieBannerEnabled = this.default.analytics.cookieBannerEnabled;
        if (env[`PT__${keyId}__ANALYTICS_COOKIE_BANNER_ENABLED`] === 'true') {
          isCookieBannerEnabled = true;
        }
        let isThirdPartyCookiesEnabled = this.default.analytics.thirdPartyCookiesEnabled;
        if (env[`PT__${keyId}__ANALYTICS_THIRD_PARTY_COOKIES_ENABLED`] === 'true') {
          isThirdPartyCookiesEnabled = true;
        }
        const analytics: ClientConfigAnalytics = {
          enabled: isAnalyticsEnabled,
          solution: analyticsSolution as ClientConfigAnalyticsSolution,
          clientId: analyticsClientId || '',
          cookieBannerEnabled: isCookieBannerEnabled,
          thirdPartyCookiesEnabled: isThirdPartyCookiesEnabled
        };

        /**
         * Contact form
         */
        let isRecaptchaEnabled = this.default.contactForm.recaptchEnabled;
        if (env[`PT__${keyId}__CONTACT_FORM_RECAPTCHA_ENABLED`] === 'true') {
          isRecaptchaEnabled = true;
        }
        let recaptchaSiteKey =
          env[`PT__${keyId}__CONTACT_FORM_RECAPTCHA_SITE_KEY`] ||
          this.default.contactForm.recaptchaSiteKey;
        let recaptchaSecretKey =
          env[`PT__${keyId}__CONTACT_FORM_RECAPTCHA_SECRET_KEY`] ||
          this.default.contactForm.recaptchaSecretKey;
        if (process.env.NODE_ENV === 'development' && isRecaptchaEnabled) {
          log.debug('Using test captcha keys');
          recaptchaSiteKey = CAPTCHA_TEST_KEYS.siteKey;
          recaptchaSecretKey = CAPTCHA_TEST_KEYS.secretKey;
        }

        const contactForm: ClientConfigContactForm = {
          recaptchEnabled: isRecaptchaEnabled,
          recaptchaSiteKey: recaptchaSiteKey || '',
          recaptchaSecretKey: recaptchaSecretKey || ''
        };

        /**
         * SMTP
         */
        const smtpUsername = env[`PT__${keyId}__SMTP_USERNAME`] || this.default.smtp.username;
        const smtpPassword = env[`PT__${keyId}__SMTP_PASSWORD`] || this.default.smtp.password;
        const smtpHost = env[`PT__${keyId}__SMTP_HOST`] || this.default.smtp.host;
        const smtpPort = env[`PT__${keyId}__SMTP_PORT`]
          ? this.int(env[`PT__${keyId}__SMTP_PORT`])
          : this.default.smtp.port;
        let isSmptSecure = this.default.smtp.secure;
        if (env[`PT__${keyId}__SMTP_SECURE`] !== undefined) {
          isSmptSecure = this.bool(env[`PT__${keyId}__SMTP_SECURE`]);
        }
        const smtpSenderEmail = env[`PT__${keyId}__SMTP_SENDER_EMAIL`];
        // if (!smtpSenderEmail) {
        //   log.error(`Missing SMTP_SENDER_EMAIL for client ${keyId}`);
        //   break;
        // }
        const smtpSenderName = env[`PT__${keyId}__SMTP_SENDER_NAME`];
        if (!smtpSenderName) {
          log.error(`Missing SMTP_SENDER_NAME for client ${keyId}`);
          break;
        }
        const smtpRecipientEmails = this.array(env[`PT__${keyId}__SMTP_RECIPIENT_EMAILS`]);
        if (smtpRecipientEmails.length === 0) {
          log.error(`Missing SMTP_RECIPIENT_EMAILS for client ${keyId}`);
          break;
        }

        const smtp: ClientConfigSmtp = {
          username: smtpUsername,
          password: smtpPassword,
          host: smtpHost,
          port: smtpPort,
          secure: isSmptSecure,
          senderEmail: smtpSenderEmail ?? smtpUsername,
          senderName: smtpSenderName,
          recipientEmails: smtpRecipientEmails
        };

        /**
         * googleSiteVerificationCode
         */
        let googleSiteVerificationCode: string | false = false;
        if (env[`PT__${keyId}__GOOGLE_SITE_VERIFICATION_CODE`]) {
          googleSiteVerificationCode = env[`PT__${keyId}__GOOGLE_SITE_VERIFICATION_CODE`] || false;
        }

        /**
         * AI Chatbot
         */
        const aiChatbot: AiChatbotConfig = {
          enabled:
            this.bool(env[`PT__${keyId}__AI_CHATBOT_ENABLED`]) === true
              ? true
              : this.default.aiChatbot.enabled,
          apiUrl: env[`PT__${keyId}__AI_CHATBOT_API_URL`]
        };
        if (aiChatbot.enabled && !aiChatbot.apiUrl) {
          log.error(`Missing AI_CHATBOT_API_URL for client ${keyId}`);
          aiChatbot.enabled = false;
        }
        if (aiChatbot.enabled) {
          aiChatbot.apiUrlHealth = env[`PT__${keyId}__AI_CHATBOT_API_URL_HEALTH`];
        }
        if (aiChatbot.enabled && !aiChatbot.apiUrlHealth) {
          log.error(`Missing AI_CHATBOT_API_URL_HEALTH for client ${keyId}`);
          aiChatbot.enabled = false;
        }

        /**
         * Canonical host (optional)
         */
        const canonicalHost = env[`PT__${keyId}__CANONICAL_HOST`];

        /**
         * ODOO
         */
        const odooUrl = env[`PT__${keyId}__ODOO_URL`] ?? this.default.odoo?.url;
        const odooDb = env[`PT__${keyId}__ODOO_DB`] ?? this.default.odoo?.db;
        const odooUsername = env[`PT__${keyId}__ODOO_USERNAME`] ?? this.default.odoo?.username;
        const odooOptiComesFrom =
          env[`PT__${keyId}__ODOO_OPTI_COMES_FROM`] ?? this.default.odoo?.optiComesFrom;
        const odooApiKey = env[`PT__${keyId}__ODOO_API_KEY`] ?? this.default.odoo?.apiKey;
        const odooTeamId = this.int(env[`PT__${keyId}__ODOO_TEAM_ID`]);
        const odooUserId = this.int(env[`PT__${keyId}__ODOO_USER_ID`]);
        const odooMailingListId = this.int(env[`PT__${keyId}__ODOO_MAILING_LIST_ID`]);

        let odoo: OdooConfig | undefined = undefined;
        if (odooUrl && odooDb && odooUsername && odooApiKey && odooTeamId && odooUserId) {
          odoo = {
            url: odooUrl,
            db: odooDb,
            username: odooUsername,
            apiKey: odooApiKey,
            optiComesFrom: odooOptiComesFrom,
            teamId: odooTeamId,
            userId: odooUserId,
            mailingListId: odooMailingListId
          };
        } else {
          const missingOdooKeys = Object.entries({
            ODOO_URL: odooUrl,
            ODOO_DB: odooDb,
            ODOO_USERNAME: odooUsername,
            ODOO_API_KEY: odooApiKey,
            ODOO_TEAM_ID: odooTeamId,
            ODOO_USER_ID: odooUserId
          })
            .filter(([, value]) => !value)
            .map(([key]) => `PT__${keyId}__${key}`);

          log.warn(
            `Incomplete ODOO config for client ${keyId}, skipping ODOO config. Missing: ${missingOdooKeys.join(', ')}`
          );
        }

        const clientConfig: ClientConfig = {
          tenantUid: id,
          hosts,
          languages,
          robotsEnabled: isRobotsEnabled,
          search,
          analytics,
          contactForm,
          smtp,
          googleSiteVerificationCode,
          aiChatbot,
          canonicalHost,
          odoo
        };

        clientConfigs.push(clientConfig);
      }
    }

    return clientConfigs;
  }

  private bool(value: string | undefined): boolean {
    return value === 'true' || value === '1' || value === 'yes';
  }

  private int(value: string | undefined): number {
    const parsed = parseInt(value || '', 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  private array(value: string | undefined): string[] {
    return (value || '')
      .split(',')
      .map(item => item.trim())
      .filter(item => item !== '');
  }
}

const configService = new ConfigService();

export { configService as config };
